/**
 * author-sphere.ts — sfera 3D condivisa per i nodi-autore (Mappa dei temi e
 * Rete delle influenze).
 *
 * LOGICA COLORE = PERIODO letterario dell'autore: ogni epoca ha la sua tinta,
 * così le sfere non sono più tutte uguali e l'epoca si legge a colpo d'occhio
 * (e si accorda con le fasce dei secoli della timeline). Il verde brand resta
 * riservato a selezione/evidenziazione (anelli), non al riempimento.
 *
 * Le chiavi sono i `Period` di `src/data/schema.ts`. Vanno tenute in sync: una
 * chiave che non corrisponde manda l'autore sul FALLBACK, e le sfere tornano
 * tutte dello stesso colore senza che niente segnali l'errore. È già successo:
 * questo file aveva ereditato i periodi della letteratura inglese dal golden
 * (`victorian`, `colonial`, `american-realism`...), che qui non esistono, e
 * ogni autore cadeva sul fallback.
 *
 * PROFONDITÀ 3D (marcata): gradiente radiale con highlight caldo in alto-sx,
 * terminatore molto scuro in basso-dx, riflesso speculare a due livelli e una
 * "luce di rimbalzo" sul bordo basso (il dettaglio che dà la rotondità) + ombra
 * portata profonda. Helper esportati così entrambe le mappe rendono identico.
 */

import type { Period } from '@/data/schema';

/** Tinta per periodo (mid-tone; il gradiente schiarisce/scurisce da qui).
 *  Nove tinte per i nove `Period` italiani, tutte alla stessa luminosità così
 *  nessuna epoca "salta" rispetto alle altre. Niente bordeaux: è l'identità
 *  del tool di inglese.
 *
 *  Il tipo è `Record<Period, string>` di proposito: se qualcuno aggiunge un
 *  periodo a `schema.ts` senza dargli una tinta, `tsc` non compila. È la rete
 *  che mancava quando il file portava ancora i periodi inglesi. */
const PERIOD_COLOR: Record<Period, string> = {
  'origini-duecento':       '#5f6b78', // peltro — le origini, ancora indistinte
  trecento:                 '#3f7d6a', // verde viridian — le tre corone
  'umanesimo-rinascimento': '#b0863a', // oro antico — la corte e l'alloro
  'seicento-barocco':       '#7e6a9c', // ametista — la meraviglia
  'settecento-illuminismo': '#4f8a8b', // ottanio — la ragione, tinta fredda
  'primo-ottocento':        '#b26a47', // terracotta — il Romanticismo
  'secondo-ottocento':      '#6f7d45', // oliva — il vero, la terra, il Verismo
  'primo-novecento':        '#46688f', // blu acciaio — le avanguardie
  'secondo-novecento':      '#6a6a72', // grafite — il dopoguerra
};
const FALLBACK = '#6f5a60';

/** Colore-sfera di un autore dal suo primo periodo (fallback neutro). */
export function authorSphereColor(periods: readonly string[] | undefined): string {
  const p = periods && periods[0];
  return (p && PERIOD_COLOR[p as Period]) || FALLBACK;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix(a: [number, number, number], b: [number, number, number], t: number): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}
const lighten = (hex: string, t: number) => mix(hexToRgb(hex), [255, 255, 255], t);
const darken = (hex: string, t: number) => mix(hexToRgb(hex), [0, 0, 0], t);

/** id stabile di gradiente da un colore (per i <defs>). */
export function sphereGradId(hex: string): string { return 'asph-' + hex.replace('#', ''); }

/** Stop del gradiente sfera per una tinta (riuso DOM + stringa). */
export function sphereStops(hex: string): Array<{ offset: string; color: string }> {
  return [
    { offset: '0%', color: lighten(hex, 0.74) },
    { offset: '15%', color: lighten(hex, 0.36) },
    { offset: '54%', color: hex },
    { offset: '100%', color: darken(hex, 0.56) },
  ];
}
/** Colore della luce di rimbalzo sul bordo basso. */
export const sphereRim = (hex: string) => lighten(hex, 0.62);

/** <defs> con un gradiente per ogni tinta usata + l'ombra condivisa (id auth-sh). */
export function authorSphereDefs(colors: string[]): string {
  const uniq = [...new Set(colors)];
  const grads = uniq.map((c) => `<radialGradient id="${sphereGradId(c)}" cx="33%" cy="25%" r="84%">
      ${sphereStops(c).map((s) => `<stop offset="${s.offset}" stop-color="${s.color}"/>`).join('')}
    </radialGradient>`).join('');
  const filt = `<filter id="auth-sh" x="-70%" y="-70%" width="240%" height="240%">
      <feDropShadow dx="0" dy="3.5" stdDeviation="4" flood-color="#120d0b" flood-opacity="0.40"/>
    </filter>`;
  return grads + filt;
}

/** Sfera autore (stringa SVG) con la tinta `baseHex` (vedi authorSphereDefs). */
export function authorSphere(cx: number, cy: number, r: number, ini: string, fp: number, baseHex: string): string {
  const rim = sphereRim(baseHex);
  const safe = ini.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
  return `<g>
    <circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#${sphereGradId(baseHex)})" filter="url(#auth-sh)"/>
    <ellipse cx="${cx - r * 0.30}" cy="${cy - r * 0.36}" rx="${r * 0.48}" ry="${r * 0.32}" fill="rgba(255,255,255,0.50)" pointer-events="none"/>
    <ellipse cx="${cx - r * 0.34}" cy="${cy - r * 0.42}" rx="${r * 0.18}" ry="${r * 0.12}" fill="rgba(255,255,255,0.92)" pointer-events="none"/>
    <path d="M ${cx - r * 0.66} ${cy + r * 0.48} A ${r * 0.82} ${r * 0.82} 0 0 0 ${cx + r * 0.66} ${cy + r * 0.48}"
          fill="none" stroke="${rim}" stroke-opacity="0.66" stroke-width="${r * 0.13}" stroke-linecap="round" pointer-events="none"/>
    <text x="${cx}" y="${cy + fp * 0.34}" text-anchor="middle" font-family="Fraunces, serif" font-weight="700" font-size="${fp}" fill="#fff" pointer-events="none">${safe}</text>
  </g>`;
}
